import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AccountListItemDto } from '@scala-watch/shared';
import { api, setActiveAccountIdGetter } from '@/lib/api';
import { isBootstrapDefaultAccount } from '@/lib/bootstrap-account';
import { useAuthStore } from './auth';
import type { AccountDto } from '@scala-watch/shared';

export const useAccountStore = defineStore('account', () => {
  const auth = useAuthStore();
  const activeAccount = ref<AccountDto | null>(null);
  const allAccountsMode = ref(false); // super-admin only

  const slug = computed(() => activeAccount.value?.slug ?? null);
  const id = computed(() => activeAccount.value?.id ?? null);

  // Wire the api helper so it auto-attaches X-Account-Id.
  setActiveAccountIdGetter(() => {
    if (allAccountsMode.value) return null;
    if (
      auth.isSuperAdmin &&
      (isBootstrapDefaultAccount(activeAccount.value) ||
        (!activeAccount.value && isBootstrapDefaultAccount(auth.activeAccount)))
    ) {
      return null;
    }
    return id.value;
  });

  async function setActive(account: AccountDto | null): Promise<void> {
    activeAccount.value = account;
    allAccountsMode.value = false;
  }

  async function switchBySlug(slug: string): Promise<void> {
    if (slug === '_') {
      return;
    }

    if (auth.isSuperAdmin && slug === 'default') {
      enableAllAccountsMode();
      return;
    }

    const match = auth.memberships.find((m) => m.account.slug === slug)?.account ?? null;
    if (match) {
      await setActive(match);
    } else if (auth.isSuperAdmin) {
      try {
        const res = await api<{ accounts: AccountListItemDto[] }>('/api/accounts');
        const found = res.accounts.find((acc) => acc.slug === slug) ?? null;
        if (found) {
          await setActive(found);
        }
      } catch {
        /* no-op */
      }
    }
  }

  function enableAllAccountsMode(): void {
    if (!auth.isSuperAdmin) return;
    allAccountsMode.value = true;
    activeAccount.value = null;
  }

  const showingAllAccounts = computed(
    () =>
      allAccountsMode.value ||
      (auth.isSuperAdmin &&
        (isBootstrapDefaultAccount(activeAccount.value) ||
          (!activeAccount.value && isBootstrapDefaultAccount(auth.activeAccount)))),
  );

  return {
    activeAccount,
    allAccountsMode,
    showingAllAccounts,
    slug,
    id,
    setActive,
    switchBySlug,
    enableAllAccountsMode,
  };
});
